import type { Filter } from "@/lib/components/chip-filter/chip-filter";
import { authManager } from "@/lib/managers/auth-manager";
import { convertUtcDateValueToDateWithoutTimezone } from "@/lib/utils/date-2";
import type { BryntumGantt } from "@bryntum/gantt-react-thin";
import type { CalendarModel, TaskModel, TaskModelConfig } from "@bryntum/gantt-thin";
// Bryntum v7 CSS imports are in gantt-container.tsx
import { calculateColorForAllocation } from "@laborchart-modules/common/dist/charts/processors/utils/gantt";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime/date";
import { getDurationHours } from "@laborchart-modules/common/dist/datetime/time";
import type {
   CachedEquipment,
   CachedEquipmentCompanyType,
} from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import type { GetPeopleGanttResponse } from "@laborchart-modules/lc-core-api/dist/api/assignments/get-people-gantt";
import { colors } from "@procore/core-react";
import { timeParse } from "d3-time-format";
import { getCalendarName } from "./gantt-calendar";
import { INITIAL_GANTT_FILTER } from "./gantt-constants";
import { getGanttConfigurePanelValues } from "./gantt-config-panel";
import "./gantt-container.css";

import type {
   GanttProject,
   GroupedTasksWrapper,
   Task,
   TaskLookup,
   ganttFilterType,
   noSubcategoryTasks,
   projectStatus,
} from "./prop-types";
import { GanttViewMode } from "./prop-types";
import { AssignmentBarColors, TaskType } from "./prop-types";
import { formatDateBryntum, formatTimeBryntum } from "./gantt-container";
import type { useI18nContext } from "@procore/core-react";
import { getTimeOffReasons } from "../tearsheets/time-off/constants";

export const getTimeRangesFeature = () => ({
   showCurrentTimeLine: {
      name: "Today",
      cls: "gantt-current-timeline",
   },
   tooltipTemplate: ({ timeRange }: any) => {
      const dateString = formatDateBryntum(timeRange.startDate);
      const timeString = formatTimeBryntum(new Date().toLocaleTimeString());
      return dateString + " - " + timeString;
   },
});

export const sortPriorityLevels = {
   [TaskType.PROJECT]: 0,
   [TaskType.ASSIGNMENT]: 1,
   [TaskType.EQUIPMENT_ASSIGNMENT]: 2,
   [TaskType.REQUEST]: 3,
   [TaskType.EQUIPMENT_REQUEST]: 4,
   [TaskType.CATEGORY]: 5,
   [TaskType.SUBCATEGORY]: 6,
};

export const projectStatuses: projectStatus[] = [
   { id: 0, name: "Active" },
   { id: 1, name: "Inactive" },
   { id: 2, name: "Pending" },
];

export const rawAssignmentToTask = ({
   assignment,
   person,
   project,
   jobTitles,
}: {
   assignment: any;
   person: any;
   project: any;
   jobTitles?: any[];
}) => {
   const dateParse = timeParse("%Y-%m-%d");
   const jobTitle =
      person.job_title ?? jobTitles?.find((jobTitle: any) => jobTitle?.id === person?.job_title_id);

   const startDate =
      typeof assignment.start_day === "string"
         ? dateParse(assignment.start_day)
         : assignment.start_day;
   let endDate =
      typeof assignment.end_day === "string" ? dateParse(assignment.end_day) : assignment.end_day;
   endDate = new Date(endDate.setHours(23, 59, 59, 999));

   const firstName = person.first_name ?? person.name.first;
   const lastName = person.last_name ?? person.name.last;
   const lastNamesFirst = authManager.authedUser()?.preferences()?.displayLastNamesFirst();

   const assignmentTask: Task = {
      id: assignment.id,
      type: TaskType.ASSIGNMENT,
      startDate,
      endDate,
      startTime: assignment.start_time,
      endTime: assignment.end_time,
      name: lastNamesFirst ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`,
      projectId: project.id,
      projectColor: project.color,
      projectName: project.name,
      projectJobNumber: project.job_number,
      parentCategoryId: assignment.cost_code_id,
      parentCategoryName: assignment.cost_code?.name,
      parentSubcategoryId: assignment.label_id,
      parentSubcategoryName: assignment.label?.name,
      jobTitleId: jobTitle?.id,
      jobTitleColor: jobTitle?.color,
      jobTitleName: jobTitle?.name,
      workDays: assignment.work_days,
      calendar: getCalendarName(
         assignment,
         TaskType.ASSIGNMENT,
         person.time_off?.length > 0,
      ) as CalendarModel & string,
      manuallyScheduled: true,
      sortPriority: sortPriorityLevels[TaskType.ASSIGNMENT],
      categoryId: assignment.cost_code_id ?? assignment.category_id ?? null,
      subcategoryId: assignment.label_id ?? assignment.subcategory_id ?? null,
      resourceId: person.id,
      overtime: assignment.overtime ?? false,
      status: assignment.status,
      percentAllocated: assignment.percent_allocated,
      resourceName: firstName + " " + lastName,
      instructionText: assignment.instruction_text,
      workScopeText: assignment.work_scope_text,
      ...(assignment.segments && {
         segments:
            typeof assignment.segments === "string"
               ? JSON.parse(assignment.segments)
               : assignment.segments,
      }),
   };
   return assignmentTask;
};

export const rawEquipmentAssignmentToTask = ({
   equipmentItem,
   project,
   equipment,
}: {
   equipmentItem: any;
   project: any;
   equipment: CachedEquipment[];
}) => {
   const dateParse = timeParse("%Y-%m-%d");
   const currentEquipment = equipment.find(
      (item: CachedEquipment) => item.id == equipmentItem.resource_id,
   );

   const startDate =
      typeof equipmentItem.start_day === "string"
         ? dateParse(equipmentItem.start_day)
         : equipmentItem.start_day;
   let endDate =
      typeof equipmentItem.end_day === "string"
         ? dateParse(equipmentItem.end_day)
         : equipmentItem.end_day;
   endDate = new Date(endDate.setHours(23, 59, 59, 999));

   const equipmentAssignmentTask: Task = {
      id: equipmentItem.id,
      type: TaskType.EQUIPMENT_ASSIGNMENT,
      startDate,
      endDate,
      startTime: equipmentItem.start_time,
      endTime: equipmentItem.end_time,
      name: currentEquipment?.equipment_name ?? "Equipment Name",
      projectId: project.id,
      projectColor: project.color,
      projectName: project.name,
      projectJobNumber: project.job_number,
      parentCategoryId: equipmentItem.cost_code_id,
      parentCategoryName: equipmentItem.cost_code?.name,
      parentSubcategoryId: equipmentItem.label_id,
      parentSubcategoryName: equipmentItem.label?.name,
      percentAllocated: equipmentItem.percent_allocated,
      jobTitleName: currentEquipment?.type_name ?? "Equipment Type",
      equipmentTypeName: currentEquipment?.type_name ?? "Equipment Type",
      workDays: equipmentItem.work_days,
      calendar: getCalendarName(equipmentItem, TaskType.EQUIPMENT_ASSIGNMENT) as CalendarModel &
         string,
      manuallyScheduled: true,
      sortPriority: sortPriorityLevels[TaskType.EQUIPMENT_ASSIGNMENT],
      categoryId: equipmentItem.cost_code_id ?? equipmentItem.category_id ?? null,
      subcategoryId: equipmentItem.label_id ?? equipmentItem.subcategory_id ?? null,
      resourceId: equipmentItem.resource_id ?? null,
   };

   return equipmentAssignmentTask;
};

/**
 * Converts a float time to a string (e.g., 14.5 to "14:30:00")
 * @param time - The time to convert.
 * @returns A string in the form HH:MM:00 or undefined
 */
export const timeFloatToString = (time: number | null | undefined): string | undefined => {
   if (typeof time === "number") {
      const hours = Math.floor(time).toString().padStart(2, "0");
      const minutes = Math.floor((time % 1) * 60)
         .toString()
         .padStart(2, "0");
      return `${hours}:${minutes}:00`;
   }
   return time ?? undefined;
};

export const rawRequestToTask = ({
   request,
   project,
   jobTitles,
}: {
   request: any;
   project: any;
   jobTitles?: any[];
}) => {
   try {
      const dateParse = timeParse("%Y-%m-%d");
      const jobTitle =
         request.job_title ??
         jobTitles?.find(
            (jobTitle: any) => jobTitle.id == (request.job_title_id ?? request.position_id),
         );

      const isDetachedDay =
         typeof request.start_day === "number" && String(request.start_day).length === 8;
      if (isDetachedDay) {
         request.start_day = getAttachedDate(request.start_day);
         request.end_day = getAttachedDate(request.end_day);
      } else if (typeof request.start_day === "string" && typeof request.end_day === "string") {
         request.start_day = dateParse(request.start_day)!;
         request.end_day = dateParse(request.end_day)!;
      } else {
         request.start_day = new Date(request.start_day);
         request.end_day = new Date(request.end_day);
      }

      const requestTask: Task = {
         id: request.id,
         type: TaskType.REQUEST,
         startDate: request.start_day,
         endDate: new Date(request.end_day.setHours(23, 59, 59, 999)), // request bar should end at the end of the day
         startTime: timeFloatToString(request.start_time),
         endTime: timeFloatToString(request.end_time),
         name: "Request",
         projectId: project.id,
         projectColor: project.color,
         projectName: project.name,
         projectJobNumber: project.job_number,
         parentCategoryId: request.cost_code_id,
         parentCategoryName: request.cost_code?.name,
         parentSubcategoryId: request.label_id,
         parentSubcategoryName: request.label?.name,
         percentAllocated: request.percent_allocated,
         jobTitleColor: jobTitle?.color,
         jobTitleName: jobTitle?.name,
         workDays: request.work_days,
         calendar: getCalendarName(request, TaskType.REQUEST) as CalendarModel & string,
         manuallyScheduled: true,
         sortPriority: sortPriorityLevels[TaskType.REQUEST],
         categoryId: request.cost_code_id ?? request.category_id ?? null,
         subcategoryId: request.label_id ?? request.subcategory_id ?? null,
         instructionText: request.instruction_text,
         workScopeText: request.work_scope_text,
         tagIds: request.tag_ids,
      };
      return requestTask;
   } catch (error) {
      console.error("error in rawRequestToTask:", error);
   }
};

export const rawEquipmentRequestToTask = ({
   request,
   project,
   equipmentTypes,
}: {
   request: any;
   project: any;
   equipmentTypes?: any[];
}) => {
   try {
      const dateParse = timeParse("%Y-%m-%d");
      const equipmentType = equipmentTypes?.find(
         (equipmentType: any) => equipmentType.id === request.type_id,
      );

      const isDetachedDay =
         typeof request.start_day === "number" && String(request.start_day).length === 8;
      if (isDetachedDay) {
         request.start_day = getAttachedDate(request.start_day);
         request.end_day = getAttachedDate(request.end_day);
      } else if (typeof request.start_day === "string" && typeof request.end_day === "string") {
         request.start_day = dateParse(request.start_day)!;
         request.end_day = dateParse(request.end_day)!;
      } else {
         request.start_day = new Date(request.start_day);
         request.end_day = new Date(request.end_day);
      }

      const requestTask: Task = {
         id: request.id,
         type: TaskType.EQUIPMENT_REQUEST,
         startDate: request.start_day,
         endDate: new Date(request.end_day.setHours(23, 59, 59, 999)),
         startTime: timeFloatToString(request.start_time),
         endTime: timeFloatToString(request.end_time),
         name: "Equipment Request",
         projectId: project.id,
         projectColor: project.color,
         projectName: project.name,
         projectJobNumber: project.job_number,
         parentCategoryId: request.cost_code_id,
         parentCategoryName: request.cost_code?.name,
         parentSubcategoryId: request.label_id,
         parentSubcategoryName: request.label?.name,
         percentAllocated: request.percent_allocated,
         jobTitleName: equipmentType?.name,
         typeId: request.type_id,
         equipmentTypeName: equipmentType?.name,
         workDays: request.work_days,
         calendar: getCalendarName(request, TaskType.EQUIPMENT_REQUEST) as CalendarModel & string,
         manuallyScheduled: true,
         sortPriority: sortPriorityLevels[TaskType.EQUIPMENT_REQUEST],
         categoryId: request.cost_code_id ?? request.category_id ?? null,
         subcategoryId: request.label_id ?? request.subcategory_id ?? null,
      };
      return requestTask;
   } catch (error) {
      console.error("error in rawEquipmentRequestToTask:", error);
   }
};

export function groupTasks(
   project: GanttProject,
   people: any,
   jobTitles: any[],
   equipment: CachedEquipment[],
   equipmentTypes: CachedEquipmentCompanyType[],
   expandedTasks: Set<string>,
   isHideEmptyCategoriesFilterEnabled = false,
): GroupedTasksWrapper {
   let earliestTaskStartDate: Date | null = null; //new Date(project.start_date);
   let latestTaskEndDate: Date | null = null; //new Date(project.est_end_date);
   const categories = project.cost_codes;
   const assignments = project.assignments;
   const requests = project.requests;
   const equipmentRequests = project.equipment_requests ?? [];
   const equipmentAssignments = project.equipment_assignments ?? [];

   // Step 1: Create a nested lookup table for tasks
   const taskLookup: TaskLookup = {};
   const noCategoryTasks: Task[] = [];
   const noSubcategoryTasks: noSubcategoryTasks = {};

   const group = (task: Task) => {
      const { categoryId, subcategoryId } = task;

      if (categoryId === null) {
         noCategoryTasks.push(task);
      } else {
         if (!taskLookup[categoryId]) {
            taskLookup[categoryId] = {};
         }
         if (subcategoryId === null) {
            if (!noSubcategoryTasks[categoryId]) {
               noSubcategoryTasks[categoryId] = [];
            }
            noSubcategoryTasks[categoryId].push(task);
         } else {
            if (!taskLookup[categoryId][subcategoryId]) {
               taskLookup[categoryId][subcategoryId] = [];
            }
            taskLookup[categoryId][subcategoryId].push(task);
         }
      }
   };

   assignments.forEach((assignment) => {
      const person = people.find((person: any) => person.id == assignment.resource_id) as any;

      if (person == null) {
         console.error("person not found for assignment:", assignment);
      } else {
         const assignmentTask = rawAssignmentToTask({ assignment, person, project, jobTitles });
         const startDate = assignmentTask.startDate as Date;
         const endDate = assignmentTask.endDate as Date;

         if (earliestTaskStartDate == null || startDate < earliestTaskStartDate)
            earliestTaskStartDate = startDate;
         if (latestTaskEndDate == null || endDate > latestTaskEndDate) latestTaskEndDate = endDate;

         group(assignmentTask);
      }
   });

   equipmentAssignments.forEach((equipmentItem) => {
      const equipmentAssignmentTask = rawEquipmentAssignmentToTask({
         equipmentItem,
         project,
         equipment,
      });
      const startDate = equipmentAssignmentTask!.startDate as Date;
      const endDate = equipmentAssignmentTask!.endDate as Date;

      if (earliestTaskStartDate == null || startDate < earliestTaskStartDate)
         earliestTaskStartDate = startDate;
      if (latestTaskEndDate == null || endDate > latestTaskEndDate) latestTaskEndDate = endDate;
      group(equipmentAssignmentTask);
   });

   requests.forEach((request) => {
      const requestTask = rawRequestToTask({ request, project, jobTitles });
      const startDate = requestTask!.startDate as Date;
      const endDate = requestTask!.endDate as Date;

      if (earliestTaskStartDate == null || startDate < earliestTaskStartDate)
         earliestTaskStartDate = startDate;
      if (latestTaskEndDate == null || endDate > latestTaskEndDate) latestTaskEndDate = endDate;
      group(requestTask!);
   });

   equipmentRequests.forEach((request) => {
      const equipmentRequestTask = rawEquipmentRequestToTask({ request, project, equipmentTypes });
      const startDate = equipmentRequestTask!.startDate as Date;
      const endDate = equipmentRequestTask!.endDate as Date;

      if (earliestTaskStartDate == null || startDate < earliestTaskStartDate)
         earliestTaskStartDate = startDate;
      if (latestTaskEndDate == null || endDate > latestTaskEndDate) latestTaskEndDate = endDate;
      group(equipmentRequestTask!);
   });

   // Step 2: Organize tasks into the desired structure
   const groupedCategories =
      categories
         ?.sort((prev, next) => prev.sequence - next.sequence)
         ?.map((category) => {
            const groupedSubcategories = category.labels
               ?.sort((prev, next) => prev.sequence - next.sequence)
               .map((subcategory) => {
                  if (subcategory == null) {
                     console.error(
                        "subcategory is null. supposed to belong to category:",
                        category,
                     );
                  }
                  const children =
                     (taskLookup[category.id] && taskLookup[category.id][subcategory.id]) || [];

                  const isHidden = isHideEmptyCategoriesFilterEnabled && !children.length;

                  return (
                     !isHidden && {
                        id: subcategory?.id,
                        archived: subcategory?.archived,
                        categoryId: category.id,
                        name: subcategory?.name ?? null,
                        subcategoryName: subcategory?.name ?? null,
                        categoryName: category.name,
                        projectId: project.id,
                        projectColor: project.color,
                        parentCategoryId: category.id,
                        parentCategoryName: category.name,
                        projectName: project.name,
                        projectJobNumber: project.job_number,
                        projectConstraintResolution: "ignore",
                        type: TaskType.SUBCATEGORY,
                        expanded: expandedTasks.has(subcategory?.id),
                        children,
                        sequence: subcategory.sequence,
                        sortPriority: sortPriorityLevels[TaskType.SUBCATEGORY],
                     }
                  );
               })
               .filter((subcategory: any) => subcategory.archived === false);

            const children = (noSubcategoryTasks[category.id] ?? []).concat(
               groupedSubcategories as any,
            );

            const isHidden = isHideEmptyCategoriesFilterEnabled && !children.length;

            return (
               !isHidden && {
                  id: category.id,
                  archived: category.archived,
                  categoryId: category.id,
                  name: category.name,
                  projectId: project.id,
                  projectColor: project.color,
                  projectName: project.name,
                  projectJobNumber: project.job_number,
                  projectConstraintResolution: "ignore",
                  type: TaskType.CATEGORY,
                  expanded: expandedTasks.has(category?.id),
                  children,
                  sequence: category.sequence,
                  sortPriority: sortPriorityLevels[TaskType.CATEGORY],
               }
            );
         })
         .filter((category: any) => category.archived === false) ?? [];

   return {
      tasks: noCategoryTasks.concat(groupedCategories as any),
      earliestTaskStartDate,
      latestTaskEndDate,
   };
}

export function parseTimeToHours(timeString: string): number {
   const [hours, minutes] = timeString.split(":").map(Number);
   return hours + minutes / 60;
}

export function getSelectedBarColorHexValues(task: TaskModel, paidShiftHours: number) {
   const assignmentBarColor = getGanttConfigurePanelValues().assignmentBarColors;
   let color = "";
   switch (assignmentBarColor) {
      case AssignmentBarColors.PROJECT:
         color = task.getData("projectColor");
         break;
      case AssignmentBarColors.ALLOCATION_PERCENTAGE:
         if (task.getData("percentAllocated")) {
            const percent = task.getData("percentAllocated");
            color = calculateColorForAllocation(Number(percent));
         } else {
            const start = parseTimeToHours(String(task.getData("startTime")));
            const end = parseTimeToHours(String(task.getData("endTime")));
            const duration = getDurationHours(start, end);
            const allocation = (duration / paidShiftHours) * 100;
            color = calculateColorForAllocation(allocation);
         }
         break;
      case AssignmentBarColors.JOB_TITLES:
         color = task.getData("jobTitleColor");
         break;
      case AssignmentBarColors.OVERTIME:
         if (task.getData("overtime")) {
            color = "#D92626";
         } else {
            color = "#40BF4A";
         }
         break;
      case AssignmentBarColors.STATUS:
         if (task.getData("status")?.color) {
            color = task.getData("status")?.color;
         } else {
            color = "#000000";
         }
         break;
   }
   return color;
}

export function transformPeopleGanttData(data: GetPeopleGanttResponse["data"]): any {
   const jobTitles: any[] = [];
   const projects: any[] = [];
   const people: any[] = [];

   data.forEach((item: any) => {
      people.push({
         id: item.id,
         employee_number: item.employee_number,
         name: item.name,
         job_title_id: item.position_data?.id ?? "",
         projects: item.projects,
         hourly_wage: item.hourly_wage,
         gender: item.gender,
         profile_pic_url: item.profile_pic_url,
         tag_instances: item.tag_instances,
         time_off: item.timeoff,
      });
      if (item.position_data) {
         const maybeJobTitle = jobTitles.find((x: any) => x.id === item.position_data?.id);
         if (!maybeJobTitle) {
            jobTitles.push(item.position_data);
         }
      }
      item.projects.forEach((project: any) => {
         const assignments = project.assignments;
         const maybeExistingProject = projects.find((x: any) => x.id === project.project_id);
         if (maybeExistingProject) {
            maybeExistingProject.assignments = maybeExistingProject.assignments.concat(assignments);
         } else {
            projects.push({
               ...project,
               equipment_assignments: [],
               requests: [],
               equipment_requests: [],
               id: project.project_id,
            });
         }
      });
   });

   const rawGanttData = {
      jobTitles,
      equipmentTypes: [],
      equipment: [],
      projects,
      people,
   };

   return rawGanttData;
}

export function getTextColorForBackground(background: string | undefined) {
   if (!background) return colors.black;
   if (background === colors.white) return colors.black;
   if (background === colors.black) return colors.white;

   // Remove the hash if it's there
   const hex = background.replace("#", "");

   // Parse the r, g, b values
   const r = parseInt(hex.substring(0, 2), 16);
   const g = parseInt(hex.substring(2, 4), 16);
   const b = parseInt(hex.substring(4, 6), 16);

   // Calculate the brightness
   const brightness = (r * 299 + g * 587 + b * 114) / 1000;

   // Return black or white depending on the brightness
   return brightness > 128 ? colors.black : colors.white;
}

/* istanbul ignore next */
export function lightenHexColor(hex: string, amount: number) {
   if (!hex) return;

   if (hex.indexOf("#") !== 0) {
      hex = "#" + hex;
   }

   const num = parseInt(hex.slice(1), 16);
   let r = num >> 16;
   let g = (num >> 8) & 0x00ff;
   let b = num & 0x0000ff;

   // Scale each component by the percentage amount towards 255
   r = Math.floor(r + (255 - r) * amount);
   g = Math.floor(g + (255 - g) * amount);
   b = Math.floor(b + (255 - b) * amount);

   // Combine back into a hex string
   return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

export function getColorShades(color: string) {
   const baseColor = color;
   const lightColor = lightenHexColor(baseColor, 0.4) ?? baseColor;
   const lightestColor = lightenHexColor(baseColor, 0.8) ?? baseColor;

   return [baseColor, lightColor, lightestColor];
}

export function createProjectTask(project: GanttProject, expandedTasks: Set<string>) {
   const isExpanded = localStorage.getItem("gantt-expanded") === "true";
   return {
      id: project.id,
      name: project.name,
      startDate: convertUtcDateValueToDateWithoutTimezone(project.start_date),
      endDate: (() => {
         const d = convertUtcDateValueToDateWithoutTimezone(project.est_end_date);
         d?.setHours(23, 59, 59, 999);
         return d;
      })(),
      // Adding prefix of "project" to "projectColor" to make it consistent with assignments & requests
      projectColor: project.color,
      projectJobNumber: project.job_number,
      dailyStartTime: project.daily_start_time,
      dailyEndTime: project.daily_end_time,
      type: TaskType.PROJECT,
      expanded: expandedTasks.has(project.id) || isExpanded,
      resizable: true,
      requestsNumber: (project.requests?.length ?? 0) + (project.equipment_requests?.length ?? 0),
      manuallyScheduled: true,
      constraintType: null,
      projectAssignments: project.assignments,
      wageOverrides: project.wage_overrides,
      workedHours: "main",
      sortPriority: sortPriorityLevels[TaskType.PROJECT],
   } as Partial<TaskModelConfig>;
}

export const clearChanges = async (gantt: React.RefObject<BryntumGantt>) =>
   await (gantt.current?.instance as any).crudManager.clearChanges();

export const suspendAutoSync = async (gantt: React.RefObject<BryntumGantt>) =>
   await (gantt.current?.instance as any).crudManager.suspendAutoSync();

export const resumeAutoSync = async (gantt: React.RefObject<BryntumGantt>) =>
   await (gantt.current?.instance as any).crudManager.resumeAutoSync();

export function getInitialFilters(savedFilters: ganttFilterType | null): ganttFilterType {
   if (
      savedFilters &&
      Object.keys(savedFilters)?.length === Object.keys(INITIAL_GANTT_FILTER).length
   ) {
      return savedFilters;
   } else {
      return {
         ...INITIAL_GANTT_FILTER,
         projectStatuses: [projectStatuses[0]], // Select active projects by default
      };
   }
}

export const getFilterFormatted = (
   filter: ganttFilterType,
   ganttViewMode: GanttViewMode,
): Record<string, Filter[]> => {
   const appliedFilters: Record<string, Filter[]> = {};
   const isProjectView = ganttViewMode === GanttViewMode.PROJECTS;
   if (filter.jobTitles?.length)
      appliedFilters["Job Titles"] = filter.jobTitles.map((jobTitle) => {
         return {
            property: "position_id",
            negation: false,
            filterName: "Job Titles",
            value: jobTitle.id,
         } as Filter;
      });

   if (filter.projectStages?.length)
      appliedFilters["Stage"] = filter.projectStages.map((projectStage) => {
         return {
            property: "cornerstone_stage",
            negation: false,
            filterName: "Stage",
            value: projectStage.id,
         } as Filter;
      });

   if (filter.projectStatuses?.length && isProjectView)
      appliedFilters["Status"] = filter.projectStatuses.map((status) => {
         return {
            property: "status",
            negation: false,
            filterName: "Status",
            value: status.name.toLowerCase(),
         } as Filter;
      });

   if (filter.projects?.length)
      appliedFilters["Projects"] = filter.projects.map((project) => {
         return {
            property: "project_id",
            negation: false,
            filterName: "Projects",
            value: project.id,
         } as Filter;
      });

   if (filter.resourceStatuses?.length && isProjectView)
      appliedFilters["Resource Status"] = filter.resourceStatuses.map((status) => {
         return {
            property: "resource_status",
            negation: false,
            filterName: "Resource Status",
            value: status.name.toLowerCase(),
         } as Filter;
      });

   if (filter.projectTags?.length)
      appliedFilters["Project Tags"] = filter.projectTags.map((tag) => {
         return {
            property: "tag_instances",
            negation: false,
            filterName: "Project Tags",
            value: [tag.id],
         } as Filter;
      });

   const hiddenEntities = [];
   if (filter.hideAssignments && isProjectView) {
      hiddenEntities.push("assignments");
   }
   if (filter.hideRequests && isProjectView) {
      hiddenEntities.push("requests");
   }

   const hasPeopleSpecificFilters =
      ganttViewMode === GanttViewMode.RESOURCES &&
      (!!filter.jobTitles?.length ||
         !!filter.projectTags?.length ||
         (filter.customFieldsFilters && Object.keys(filter.customFieldsFilters).length > 0));

   if (filter.hideTypeEquipment || hasPeopleSpecificFilters) {
      hiddenEntities.push("equipment");

      if (ganttViewMode === GanttViewMode.RESOURCES) {
         appliedFilters["Hide Empty Equipment"] = [
            {
               property: "only_equipment_with_data",
               negation: false,
               filterName: "Hide Empty Equipment",
               value: true,
            } as Filter,
         ];
      }
   }
   if (filter.hideTypeLabor) {
      hiddenEntities.push("assignments", "requests", "timeoff");

      if (ganttViewMode === GanttViewMode.RESOURCES) {
         appliedFilters["Hide Empty People"] = [
            {
               property: "only_people_with_data",
               negation: false,
               filterName: "Hide Empty People",
               value: true,
            } as Filter,
         ];
      }
   }
   if (hiddenEntities.length > 0) {
      appliedFilters["Hidden Entities"] = [
         {
            property: "hidden_entities",
            negation: false,
            filterName: "Hidden Entities",
            value: hiddenEntities,
         } as Filter,
      ];
   }

   if (filter.hidePast) {
      appliedFilters["Hide Past"] = [
         {
            property: "hide_past",
            negation: false,
            filterName: "Hide Past",
            value: true,
         } as Filter,
      ];
   }

   if (filter.hideEmptyProject && isProjectView)
      appliedFilters["Hide Empty Projects"] = [
         {
            property: "only_projects_with_data",
            negation: false,
            filterName: "Hide Empty Projects",
            value: true,
         } as Filter,
      ];

   if (filter.projectRoles?.length && isProjectView) {
      const usedJobTitleResourcePairs = new Set();
      const uniqueRoleValues: string[][] = [];

      // This usedJobTitleResourcePairs set will protect us against sending duplicate role value combinations in our GET payload.
      filter.projectRoles.forEach((role) => {
         // We'll plan for both values to support backwards compatibility.
         // Notice the type difference on role between truthy and falsy.
         let value: string[] = [];
         if ("position_id" in role) {
            value = [role.position_id, role.person_id];
         } else if ("job_title_id" in role) {
            value = [role.job_title_id, role.resource_id];
         } else if ("classifier" in role) {
            value = [role.classifier!, role.value as string];
         }

         const pair = value.join("");

         if (!usedJobTitleResourcePairs.has(pair)) {
            uniqueRoleValues.push(value);
            usedJobTitleResourcePairs.add(pair);
         }
      });

      appliedFilters["Project Roles"] = uniqueRoleValues.map((values) => {
         return {
            property: "project_roles",
            negation: false,
            filterName: "Project Roles",
            value: values,
         } as Filter;
      });
   }

   if (filter.customFieldsFilters) {
      Object.entries(filter.customFieldsFilters).forEach(([key, value]) => {
         appliedFilters[key] = value as Filter[];
      });
   }

   return appliedFilters;
};

/**
 * Subtracts one minute from the end date of a given TaskModel.
 *
 * This function is intended to address an issue where assignments that end at exactly midnight
 * may be rounded up to the next day by conflict checkers or other date calculations.
 * By subtracting one minute, we ensure that the end date does not extend into the following day unintentionally.
 *
 * @param {TaskModel} task - The task whose end date will be adjusted.
 */
export function subtractOneMinuteFromTaskEndDate(task: TaskModel) {
   const endDate = task.getData("endDate");
   if (endDate) {
      endDate.setMinutes(endDate.getMinutes() - 1);
      task.set("endDate", endDate);
   }
}

/**
 * Gets the translated time off reason if it exists in the predefined list,
 * otherwise returns the reason as-is from the backend.
 *
 * @param reason - The time off reason from the backend
 * @param I18n - The i18n context for translations
 * @returns Translated reason if found in predefined list, otherwise the original reason
 */
export function getTimeOffReasonDisplay(
   reason: string | undefined,
   I18n: ReturnType<typeof useI18nContext>,
): string {
   if (!reason) return "";

   const found = getTimeOffReasons(I18n).find(
      (r) => r.value.toLowerCase() === reason.toLowerCase(),
   );

   if (found) {
      const snake_case_value = found.value.split(" ").join("_");
      return I18n.t(`views.company.workforce_planning.time_off.reasons.${snake_case_value}`);
   }

   return reason;
}
